import {
  LoaderFunctionArgs,
  useLoaderData,
} from "react-router";
import { createAccountDMsReviewPageURL } from "#lib/urls";
import { fetchHasPendingDMs } from "#api/dms";
import { fetchImport } from "#api/imports";
import { getLocalStorageItem, setLocalStorageItem } from "#storage/local";
import { PageSkeleton } from "#components/pages";
import { LoadingIcon } from "#components/loading";
import { KemonoLink } from "#components/links";
import { Timestamp } from "#components/dates";
import { useEffect, useState } from "react";

import * as styles from "./importer_status.module.scss";

interface IProps {
  importId: string;
}

export function ImporterStatusPage() {
  const { importId } = useLoaderData() as IProps;
  const title = `Import ${importId}`;
  const heading = `Import ${importId}`;
  const cooldown = 15_000;
  const [reverse, setReverse] = useState(false);
  const [status, setStatus] = useState<Awaited<ReturnType<typeof fetchImport>> | null>(null);
  const [logs, setLogs] = useState<string[] | null>(null);
  const [minimized, setMinimized] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (status?.finished_at || error) return;
    async function fetchLogs() {
      try {
        let result = await fetchImport(importId);
        setStatus(result);
        setLogs(reverse ? [...result.logs].reverse() : [...result.logs]);
      } catch {
        setError(true);
      }
    }

    fetchLogs();

    const id = setInterval(fetchLogs, cooldown);

    return () => clearInterval(id);
  }, [reverse, error]);

  function toggleReverse() {
    setReverse(!reverse);
    setLogs(logs?.reverse() ?? []);
  }

  function toggleMinimized() {
    setMinimized(!minimized);
  }

  if (error) {
    return (
      <PageSkeleton name="importer-status" title={title} heading="Missing Import">
        <p>Your import could not be found.</p>
      </PageSkeleton>
    )
  }

  return (
    <PageSkeleton name="importer-status" title={title} heading={heading}>
      {!status ? (
        <p>
          <LoadingIcon /> <span>Wait until logs load...</span>
        </p>
      ) : (
        <>
          {status?.save_dms && (
            <div className={styles.dmNotice}>
              <strong>Hey!</strong>
              <p>
                You gave the importer permission to access your messages. To protect your anonymity, you must manually approve each one. Wait until <em>after</em> the importer says <code>Done importing DMs</code>, then go           go{" "}
                <KemonoLink url={String(createAccountDMsReviewPageURL())}>
                  here
                </KemonoLink>{" "}
                to choose which ones you wish to import. to choose which ones you wish to import.
              </p>
            </div>
          )}
          <div className={styles.importPage}>
            {status && logs && (
              <div className={styles.importStats}>
                <div>
                  <button onClick={toggleReverse} className="button" title={reverse ? "Oldest First" : "Newest First"}>
                    <img src="/static/sort.svg" alt="Sort" className={reverse ? styles.asc : styles.desc} />
                  </button>
                  <button onClick={toggleMinimized} className="button" title={minimized ? "Maximize" : "Minimize"}>
                    <img src={minimized ? "/static/maximize.svg" : "/static/minimize.svg"} alt={minimized ? "Maximize" : "Minimize"} />
                  </button>
                </div>
                <table hidden={minimized}>
                  <tr>
                    <th>Status</th>
                    <td>
                      {status.finished_at ? "Finished" : (status.is_key_invalid ? "Failed" : "In Progress")}
                    </td>
                  </tr>
                  <tr>
                    <th>Service</th>
                    <td>{status.service}</td>
                  </tr>
                  <tr>
                    <th>Priority</th>
                    <td>{status.priority}</td>
                  </tr>
                  <tr>
                    <th>Subscriptions</th>
                    <td>{status.sub_count}</td>
                  </tr>
                  {status.save_dms && (
                    <tr>
                      <th>Save DMs?</th>
                      <td>Yes</td>
                    </tr>
                  )}
                  <tr>
                    <th>Submitted at</th>
                    <td>
                      <Timestamp time={status.created_at} showHour />
                    </td>
                  </tr>
                  {status.first_started_at && (
                    <tr>
                      <th>First tried at</th>
                      <td>
                        <Timestamp time={status.first_started_at} showHour />
                      </td>
                    </tr>
                  )}
                  {status.retry_count > 0 && (
                    <tr>
                      <th>Retries</th>
                      <td>{status.retry_count}</td>
                    </tr>
                  )}
                  {status.current_try_started_at && (
                    <tr>
                      <th>Started at</th>
                      <td>
                        <Timestamp time={status.current_try_started_at} showHour />
                      </td>
                    </tr>
                  )}
                  {status.resuming_at && (
                    <tr>
                      <th>Resuming at</th>
                      <td>
                        <Timestamp time={status.resuming_at} showHour />
                      </td>
                    </tr>
                  )}
                  {status.finished_at && (
                    <tr>
                      <th>Finished at</th>
                      <td>
                        <Timestamp time={status.finished_at} showHour />
                      </td>
                    </tr>
                  )}
                  {status.key_failed_at && (
                    <tr>
                      <th>Key failed at</th>
                      <td>
                        <Timestamp time={status.key_failed_at} showHour />
                      </td>
                    </tr>
                  )}
                </table>
              </div>
            )}
            {logs?.length ? (
              <ol className={styles.importList}>
                {logs?.map((message, index) => (
                  <li key={index}>{message}</li>
                ))}
              </ol>
            ) : (
              <p>No logs found! Check again in a few minutes, or come back later.</p>
            )}
          </div>
        </>
      )}
    </PageSkeleton>
  );
}

async function initPendingReviewDms(
  forceReload = false,
  minutesForRecheck = 30
) {
  let hasPendingReviewDms = getLocalStorageItem("has_pending_review_dms") === "true";
  const lastCheckedHasPendingReviewDms = parseInt(
    getLocalStorageItem("last_checked_has_pending_review_dms") ?? "0",
    10
  );

  if (
    forceReload ||
    !lastCheckedHasPendingReviewDms ||
    lastCheckedHasPendingReviewDms < Date.now() - minutesForRecheck * 60 * 1000
  ) {
    hasPendingReviewDms = await fetchHasPendingDMs();
    setLocalStorageItem("has_pending_review_dms", String(hasPendingReviewDms));
    localStorage.setItem(
      "last_checked_has_pending_review_dms",
      Date.now().toString()
    );
  }
}

export async function loader({ params }: LoaderFunctionArgs): Promise<IProps> {
  const importId = params.import_id?.trim();
  if (!importId) {
    throw new Error("Import ID is required.");
  }

  return {
    importId,
  };
}
